import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Edit, Trash2, ShoppingCart, ExternalLink, Globe } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLOR_PRESETS = [
  { value: 'bg-amber-100 text-amber-700 hover:bg-amber-200', label: 'Amber' },
  { value: 'bg-green-100 text-green-700 hover:bg-green-200', label: 'Green' },
  { value: 'bg-blue-100 text-blue-700 hover:bg-blue-200', label: 'Blue' },
  { value: 'bg-purple-100 text-purple-700 hover:bg-purple-200', label: 'Purple' },
  { value: 'bg-rose-100 text-rose-700 hover:bg-rose-200', label: 'Rose' },
  { value: 'bg-teal-100 text-teal-700 hover:bg-teal-200', label: 'Teal' },
  { value: 'bg-orange-100 text-orange-700 hover:bg-orange-200', label: 'Orange' },
  { value: 'bg-slate-100 text-slate-700 hover:bg-slate-200', label: 'Slate' },
];

const EMPTY_FORM = {
  name: '',
  search_url_template: '',
  country_codes: '',
  color_class: COLOR_PRESETS[0].value,
  order: 100,
  enabled: true,
};

export default function RetailersTab() {
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('admin_token');
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/retailers`, authHeader);
      setRetailers(res.data.retailers || []);
    } catch (e) {
      toast.error('Failed to load retailers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({
      name: r.name,
      search_url_template: r.search_url_template,
      country_codes: (r.country_codes || []).join(', '),
      color_class: r.color_class,
      order: r.order,
      enabled: r.enabled,
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.search_url_template.includes('{query}')) {
      return toast.error('URL must include {query} placeholder');
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        search_url_template: form.search_url_template.trim(),
        country_codes: form.country_codes
          .split(',')
          .map(c => c.trim().toUpperCase())
          .filter(Boolean),
        color_class: form.color_class,
        order: Number(form.order) || 100,
        enabled: form.enabled,
      };
      if (editingId) {
        await axios.put(`${API}/admin/retailers/${editingId}`, payload, authHeader);
        toast.success('Retailer updated');
      } else {
        await axios.post(`${API}/admin/retailers`, payload, authHeader);
        toast.success('Retailer added');
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save retailer');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this retailer? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/retailers/${id}`, authHeader);
      toast.success('Retailer deleted');
      load();
    } catch {
      toast.error('Failed to delete retailer');
    }
  };

  const toggleEnabled = async (r) => {
    try {
      await axios.put(`${API}/admin/retailers/${r.id}`, {
        name: r.name,
        search_url_template: r.search_url_template,
        country_codes: r.country_codes || [],
        color_class: r.color_class,
        order: r.order,
        enabled: !r.enabled,
      }, authHeader);
      load();
    } catch {
      toast.error('Failed to toggle retailer');
    }
  };

  return (
    <div className="space-y-6" data-testid="retailers-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-primary" />
            Where to Buy &mdash; Retailers
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Manage the &ldquo;Where to Buy&rdquo; buttons shown on supplement cards. Use <code className="bg-muted px-1 rounded">{'{query}'}</code> as the placeholder for the supplement name. Leave countries blank for global, or list ISO codes (e.g. <code className="bg-muted px-1 rounded">GB, IE</code>) for geo-targeting.
          </p>
        </div>
        {!showForm && (
          <Button onClick={() => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); }} data-testid="add-retailer-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Retailer
          </Button>
        )}
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-border rounded-2xl p-5 space-y-4" data-testid="retailer-form">
          <h3 className="font-semibold">{editingId ? 'Edit Retailer' : 'Add New Retailer'}</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-1">Name *</label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Chemist Warehouse"
                data-testid="retailer-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Display Order</label>
              <Input
                type="number"
                value={form.order}
                onChange={e => setForm({ ...form, order: e.target.value })}
                placeholder="100"
                data-testid="retailer-order-input"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">
              Search URL Template * <span className="text-muted-foreground text-xs">(must include {'{query}'})</span>
            </label>
            <Input
              value={form.search_url_template}
              onChange={e => setForm({ ...form, search_url_template: e.target.value })}
              placeholder="https://www.chemistwarehouse.com.au/search?searchtext={query}"
              data-testid="retailer-url-input"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-1 flex items-center gap-1">
                <Globe className="w-3.5 h-3.5" /> Country Codes (ISO, comma-separated)
              </label>
              <Input
                value={form.country_codes}
                onChange={e => setForm({ ...form, country_codes: e.target.value })}
                placeholder="GB, IE   (leave blank for global)"
                data-testid="retailer-countries-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Button Color</label>
              <select
                value={form.color_class}
                onChange={e => setForm({ ...form, color_class: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="retailer-color-select"
              >
                {COLOR_PRESETS.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={e => setForm({ ...form, enabled: e.target.checked })}
              data-testid="retailer-enabled-checkbox"
            />
            Enabled (show on results page)
          </label>

          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={saving} data-testid="retailer-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {editingId ? 'Update' : 'Create'}
            </Button>
            <Button type="button" variant="outline" onClick={resetForm} data-testid="retailer-cancel-btn">
              Cancel
            </Button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : retailers.length === 0 ? (
        <div className="bg-muted/40 border border-dashed border-border rounded-2xl p-10 text-center text-muted-foreground text-sm">
          No retailers configured yet. Click &ldquo;Add Retailer&rdquo; to create your first one.
        </div>
      ) : (
        <div className="bg-white border border-border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Order</th>
                  <th className="text-left px-4 py-3 font-medium">Name</th>
                  <th className="text-left px-4 py-3 font-medium">URL Template</th>
                  <th className="text-left px-4 py-3 font-medium">Countries</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-right px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {retailers.map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`retailer-row-${r.id}`}>
                    <td className="px-4 py-3 text-muted-foreground">{r.order}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium ${r.color_class}`}>
                        <ExternalLink className="w-3 h-3" />
                        {r.name}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground max-w-[280px] truncate" title={r.search_url_template}>
                      {r.search_url_template}
                    </td>
                    <td className="px-4 py-3">
                      {(r.country_codes && r.country_codes.length > 0) ? (
                        <div className="flex flex-wrap gap-1">
                          {r.country_codes.map(c => (
                            <span key={c} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">{c}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-emerald-600 font-medium">Global</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleEnabled(r)}
                        className={`text-xs font-medium px-2 py-1 rounded-md ${
                          r.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                        }`}
                        data-testid={`retailer-toggle-${r.id}`}
                      >
                        {r.enabled ? 'Enabled' : 'Disabled'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => startEdit(r)}
                          className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
                          title="Edit"
                          data-testid={`retailer-edit-${r.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => remove(r.id)}
                          className="p-1.5 rounded-md hover:bg-red-50 text-muted-foreground hover:text-red-600"
                          title="Delete"
                          data-testid={`retailer-delete-${r.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
